/**
 * Tenant Context Provider
 * Manages current organization/tenant state
 */

import React, { createContext, useContext, useEffect, useState } from 'react';
import { Organization, User } from '../types/tenant';
import cfg from '../config';

interface AuthHookResult {
  isAuthenticated: boolean;
  user: any;
  getAccessToken: () => Promise<string>;
}

interface TenantContextType {
  currentTenant: Organization | null;
  currentUser: User | null;
  tenants: Organization[];
  isLoading: boolean;
  error: string | null;
  switchTenant: (tenantId: string) => void;
  refreshTenant: () => Promise<void>;
  // Platform admin tenant switching
  activeTenant: Organization | null;
  isManagingOtherTenant: boolean;
  switchToTenant: (tenant: Organization) => void;
  switchBackToOwnTenant: () => void;
}

const TenantContext = createContext<TenantContextType | undefined>(undefined);

// Internal component that always receives auth from parent
const TenantProviderInternal: React.FC<{
  children: React.ReactNode;
  auth: AuthHookResult;
}> = ({ children, auth }) => {
  const { isAuthenticated, user, getAccessToken } = auth;
  const [currentTenant, setCurrentTenant] = useState<Organization | null>(null);
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [tenants, setTenants] = useState<Organization[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Platform admin tenant switching
  const [activeTenant, setActiveTenant] = useState<Organization | null>(null);
  const [isManagingOtherTenant, setIsManagingOtherTenant] = useState(false);

  useEffect(() => {
    if (isAuthenticated && user) {
      loadUserTenant();
    } else {
      setCurrentTenant(null);
      setCurrentUser(null);
      setTenants([]);
    }
  }, [isAuthenticated, user]);

  const loadUserTenant = async () => {
    if (!user) return;

    setIsLoading(true);
    setError(null);

    try {
      const token = await getAccessToken();
      console.log('🔍 Loading tenant for user:', user.email || user.preferred_username);
      console.log('🔑 Token length:', token?.length);

      // Check for pending invite token in session storage
      const inviteToken = sessionStorage.getItem('pendingInviteToken');
      let url = `${cfg.API_BASE}/api/tenants/user`;

      if (inviteToken) {
        console.log('📧 Found pending invite token, including in request');
        url += `?inviteToken=${encodeURIComponent(inviteToken)}`;
      }

      // Try to get user's tenant from backend
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      console.log('📡 Response status:', response.status);

      if (response.ok) {
        const data = await response.json();
        console.log('✅ Received tenant data:', data);

        // Check if response has user/organization structure or is the tenant directly
        const organization = data.organization || data;
        const userInfo = data.user || {
          userId: user.userId || user.oid || user.sub,
          email: user.email || user.preferred_username || user.upn,
          displayName: user.displayName || user.name || user.email,
          role: data.role || 'member',
          tenantId: organization.tenantId,
          createdAt: new Date().toISOString(),
        };

        console.log('📦 Setting user:', userInfo);
        console.log('🏢 Setting organization:', organization);

        setCurrentUser(userInfo);
        setCurrentTenant(organization);
        setTenants([organization]); // For now, single tenant per user
      } else if (response.status === 403) {
        // Domain not approved error
        const errorData = await response.json();
        console.error('🚫 Domain not approved:', errorData);
        if (errorData.code === 'DOMAIN_NOT_APPROVED') {
          setError(`DOMAIN_NOT_APPROVED: ${errorData.message}`);
        } else {
          setError('Access denied. Your email domain is not approved for this system.');
        }
      } else if (response.status === 404) {
        // User doesn't have a tenant yet - they need to create or join one
        // Set user from auth context
        setCurrentUser({
          userId: user.userId || user.oid || user.sub,
          email: user.email || user.preferred_username || user.upn,
          displayName: user.displayName || user.name || user.email,
          role: 'member',
          tenantId: 'temp-no-org',
          createdAt: new Date().toISOString(),
        });
        const errorText = await response.text();
        console.error('❌ 404 response:', errorText);
        setError('No organization found. Please create or join an organization.');
      } else {
        const errorText = await response.text();
        console.error(`❌ Error ${response.status}:`, errorText);
        throw new Error(`Failed to load tenant: ${response.status} ${response.statusText}`);
      }
    } catch (err: any) {
      console.error('Failed to load tenant:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const switchTenant = (tenantId: string) => {
    const tenant = tenants.find(t => t.tenantId === tenantId);
    if (tenant) {
      setCurrentTenant(tenant);
      localStorage.setItem('currentTenantId', tenantId);
    }
  };

  const refreshTenant = async () => {
    await loadUserTenant();
  };

  // Platform admin: Switch to manage another tenant
  const switchToTenant = (tenant: Organization) => {
    console.log('🔧 Platform admin switching to tenant:', tenant.name);
    setActiveTenant(tenant);
    setIsManagingOtherTenant(true);
  };

  // Platform admin: Switch back to own tenant
  const switchBackToOwnTenant = () => {
    console.log('🔙 Platform admin switching back to own tenant');
    setActiveTenant(null);
    setIsManagingOtherTenant(false);
  };

  return (
    <TenantContext.Provider
      value={{
        currentTenant,
        currentUser,
        tenants,
        isLoading,
        error,
        switchTenant,
        refreshTenant,
        // Platform admin tenant switching
        activeTenant: activeTenant || currentTenant,
        isManagingOtherTenant,
        switchToTenant,
        switchBackToOwnTenant,
      }}
    >
      {children}
    </TenantContext.Provider>
  );
};

// Export different providers based on mode - this avoids conditional hooks
export const TenantProvider = TenantProviderInternal;

export const useTenant = () => {
  const context = useContext(TenantContext);
  if (context === undefined) {
    throw new Error('useTenant must be used within a TenantProvider');
  }
  return context;
};
